import { queryClient } from "@/src/config/react-query";
import { BlocksUser } from "@/src/models/user.model";
import { getToken } from "@/src/services/api/auth.api";
import {
  getNotificationConfigurations,
  NotificationConfiguration,
} from "@/src/services/api/notification/notification.service";
import { useGetUserPreferencesQuery } from "@/src/services/profile/profile.hooks";
import * as signalR from "@microsoft/signalr";
import { useEffect } from "react";
import { Platform } from "react-native";

/**
 * The channel names this file used to hardcode.
 *
 * They are kept ONLY as a fallback for when the config plane is unreachable, so a
 * config-plane hiccup degrades to the previous behaviour instead of silencing
 * notifications entirely. They are deliberately not the source of truth: a
 * hardcoded list means any notification type added in the Blocks portal is never
 * subscribed to, and so is silently never received.
 */
export const FALLBACK_CHANNELS = [
  "groupJoinRequest",
  "AddedToGroup",
  "postLiked",
  "postComment",
  "eventInvite",
  "eventCreated",
  "postShared",
  "removeFromGroup",
  "birthdayWish",
  "wikiCreated",
  "wikiUpdated",
  "newsCreated",
  "newsUpdated",
] as const;

/**
 * The `configurationName` values this app passes to `Notify`.
 *
 * These are config *names*, not SignalR event names — a different vocabulary from
 * FALLBACK_CHANNELS above. Sending one that does not exist in the tenant is the
 * silent failure: `Notify` answers HTTP 200 with `no_configuration_exist`.
 *
 * ⚠ `"Default"` does NOT belong here, and has been removed twice. It is the
 * **storage** configuration for `data/v4`'s `GetPreSignedUrlForUpload` — see
 * use-upload-file.ts and wiki.services.ts, which pass it correctly to a different
 * API that happens to share the parameter name `configurationName`. It gets swept
 * in whenever this list is rebuilt by grepping the codebase for that key. Listing
 * it makes reportUnknownConfigurationNames warn about it on every startup, burying
 * the warnings for configs that are genuinely missing from the tenant. A test pins
 * its absence.
 */
export const SENT_CONFIGURATION_NAMES = [
  "add-to-group",
  "birthday-wish",
  "event-created",
  "join-request",
  "mention",
  "news-created",
  "news-updated",
  "post-comment",
  "post-liked",
  "post-share",
  "remove-from-group",
  "wiki-created",
  "wiki-updated",
] as const;

/**
 * The v4 hub is on `logic`, alongside the Notifier REST routes.
 *
 * Confirmed 2026-08-05, not guessed: `POST .../logic/v4/NotificationHub/negotiate`
 * answers 401 while the control `POST .../logic/v4/BogusHubXyz/negotiate` returns
 * 200 HTML from the SPA catch-all, so the control discriminates. The platform's
 * notifications usage guide ships the same path with a captured wss handshake.
 *
 * The previous address, `communication/v1/NotificationHub`, was never wrong in
 * shape — the service under it died.
 */
export const HUB_URL = `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/logic/v4/NotificationHub?x-blocks-key=${process.env.EXPO_PUBLIC_X_BLOCKS_KEY}`;

/**
 * Finite retry schedule. The default `withAutomaticReconnect()` retries forever and
 * silently, which matters here: the reporter noted that when realtime last worked it
 * "took several attempts to connect", so a genuinely wrong address and a slow-but-
 * working one look identical from the outside. Ending in a logged terminal close
 * makes "connected on attempt 3" distinguishable from "never connected".
 */
export const RECONNECT_DELAYS_MS = [0, 2000, 10000, 30000] as const;

/**
 * A config's `notifyMethod` IS the SignalR event name — that is how the official
 * @seliseblocks/notifications client works (it calls `client.on(notifyMethod, …)`
 * per config). We derive subscriptions the same way rather than adopting the
 * package, to avoid a new dependency for ~10 lines; the hub wiring itself is
 * unchanged here and is CR-N3's concern.
 */
export const deriveChannels = (
  configurations: NotificationConfiguration[] | undefined,
): string[] => {
  const names = (configurations ?? [])
    .map((c) => c.notifyMethod?.trim())
    .filter((n): n is string => !!n);

  return Array.from(new Set(names));
};

/**
 * AC-N2-05 — warn when the app sends a `configurationName` the tenant does not
 * define. Without this the mismatch is invisible: the send returns 200.
 */
export const reportUnknownConfigurationNames = (
  configurations: NotificationConfiguration[] | undefined,
): string[] => {
  const defined = new Set(
    (configurations ?? [])
      .map((c) => c.name?.trim())
      .filter((n): n is string => !!n),
  );

  const unknown = SENT_CONFIGURATION_NAMES.filter((n) => !defined.has(n));
  if (unknown.length) {
    console.warn(
      "Notification configs missing in this tenant — sends using these names will " +
        "return 200 with no_configuration_exist and deliver nothing:",
      unknown,
    );
  }
  return [...unknown];
};

export const useNotificationConnection = (user?: BlocksUser | null) => {
  // Existing profiles predate `notificationsEnabled`, so `undefined` must mean
  // "on" — only an explicit `false` mutes the connection. Read via the same
  // preferences hook the settings toggle writes through, rather than
  // re-fetching the profile here.
  const { data: preferences } = useGetUserPreferencesQuery(user?.itemId ?? "");
  const notificationsEnabled = preferences?.notificationsEnabled !== false;

  useEffect(() => {
    let connection: signalR.HubConnection | null = null;
    let cancelled = false;

    const setup = async () => {
      const isWeb = Platform.OS === "web";
      const accessToken = await getToken();
      if ((!accessToken && !isWeb) || !user || !notificationsEnabled) return;

      // Resolve the channel list before opening the connection. A config-plane
      // failure must not stop us connecting — it just costs portal-added types.
      let channels: string[] = [...FALLBACK_CHANNELS];
      try {
        const config = await getNotificationConfigurations();
        const derived = deriveChannels(config?.configurations);
        if (derived.length) {
          channels = derived;
          reportUnknownConfigurationNames(config?.configurations);
        } else {
          console.warn(
            "Notification config plane returned no usable notifyMethod values; " +
              "falling back to the known channel list",
          );
        }
      } catch (e) {
        console.warn(
          "Notification config fetch failed; falling back to the known channel list",
          e,
        );
      }

      if (cancelled) return;

      connection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          // Set the transport explicitly rather than letting SignalR negotiate:
          // the platform's own client and its captured handshake both go straight
          // to a WebSocket upgrade.
          transport: signalR.HttpTransportType.WebSockets,
          ...(accessToken ? { accessTokenFactory: () => accessToken } : {}),
          withCredentials: true,
        })
        // Bounded, not the default infinite schedule — see RECONNECT_DELAYS_MS.
        .withAutomaticReconnect([...RECONNECT_DELAYS_MS])
        .build();

      const handleNotification = () => {
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      };

      channels.forEach((channel) => {
        connection?.on(channel, handleNotification);
      });

      connection.onreconnecting((e) =>
        console.warn("Notification hub: reconnecting", e),
      );
      connection.onreconnected(() => {
        console.log("Notification hub: reconnected");
        // A gap in the socket means missed events; resync rather than assume.
        handleNotification();
      });
      connection.onclose((e) =>
        console.error(
          "Notification hub: CLOSED — realtime is off for this session. " +
            "Bell, history, mark-as-read and sending are unaffected.",
          e,
        ),
      );

      try {
        console.log(`Notification hub: connecting to ${HUB_URL}`);
        await connection.start();
        console.log(
          `Notification hub: connected (${channels.length} channels subscribed)`,
        );
      } catch (e) {
        console.error(
          `Notification hub: initial connection FAILED for ${HUB_URL}. ` +
            "REST notifications still work; only live updates are lost.",
          e,
        );
      }
    };

    if (user && notificationsEnabled) setup();

    return () => {
      cancelled = true;
      connection?.stop();
    };
  }, [user?.itemId, notificationsEnabled]);
};
